import React, { useState, useEffect } from 'react';
import {
  FaHeadset,
  FaEye,
  FaTrash,
  FaFilter,
  FaExternalLinkAlt
} from 'react-icons/fa';
import SupportTicketDetail from '../../components/SupportTicketDetail';

const AdminSupportTickets = () => {
  const [supportTickets, setSupportTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isUpdatingTicket, setIsUpdatingTicket] = useState(false);

  const fetchSupportTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const supportTicketsRes = await fetch(`/api/dashboard/admin/support-tickets?page=${currentPage}&limit=20`, { headers });
      if (supportTicketsRes.ok) {
        const supportTicketsData = await supportTicketsRes.json();
        setSupportTickets(supportTicketsData.supportTickets);
        setTotalPages(supportTicketsData.totalPages);
      }
    } catch (error) {
      console.error('Error fetching support tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupportTickets();
  }, [currentPage]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => ({
    open: 'Açık',
    in_progress: 'İşlemde',
    waiting_for_customer: 'Kullanıcı Bekleniyor',
    resolved: 'Çözüldü',
    closed: 'Kapalı'
  }[status] || status || 'Bilinmiyor');

  const getStatusClass = (status) => ({
    open: 'text-red-400 bg-red-900/20 border-red-500/30',
    in_progress: 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30',
    waiting_for_customer: 'text-blue-400 bg-blue-900/20 border-blue-500/30',
    resolved: 'text-green-400 bg-green-900/20 border-green-500/30',
    closed: 'text-gray-400 bg-gray-900/20 border-gray-500/30'
  }[status] || 'text-gray-400 bg-gray-900/20 border-gray-500/30');

  const handleViewSupportTicket = (supportTicket) => {
    setSelectedTicket(supportTicket);
    setShowDetailModal(true);
  };

  const handleUpdateSupportTicket = async (ticketId, updateData) => {
    setIsUpdatingTicket(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/dashboard/admin/support-tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        // Update selected ticket if it's the same one
        if (selectedTicket && selectedTicket._id === ticketId) {
          setSelectedTicket(result.supportTicket);
        }
        setSupportTickets(prev => prev.map(ticket => (
          ticket._id === ticketId ? result.supportTicket : ticket
        )));
        // Refresh the list
        await fetchSupportTickets();
        setShowDetailModal(false);
        setSelectedTicket(null);
        return result.supportTicket;
      }
    } catch (error) {
      console.error('Update support ticket error:', error);
    } finally {
      setIsUpdatingTicket(false);
    }
  };

  const handleDeleteSupportTicket = (supportTicketId, title) => {
    setDeleteTargetId(supportTicketId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`/api/dashboard/admin/support-tickets/${deleteTargetId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setSupportTickets(prev => prev.filter(ticket => ticket._id !== deleteTargetId));
        if (supportTickets.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          await fetchSupportTickets();
        }
        setShowDeleteModal(false);
        setDeleteTargetId(null);
      }
    } catch (error) {
      console.error('Error deleting support ticket:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Support Ticket</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Kullanıcı</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Öncelik</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Durum</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Oluşturulma</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {supportTickets.map((ticket) => (
                  <tr key={ticket._id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <div className="flex items-center">
                        <FaHeadset className="h-5 w-5 text-indigo-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-white">{ticket.subject}</div>
                          <div className="text-xs text-gray-400 truncate max-w-xs">{ticket.ticketNumber}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <div className="text-sm text-white font-medium">
                        {ticket.userId?.firstName} {ticket.userId?.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${ticket.priority === 'high'
                          ? 'text-red-400 bg-red-900/20 border border-red-500/30'
                          : ticket.priority === 'medium'
                            ? 'text-yellow-400 bg-yellow-900/20 border border-yellow-500/30'
                            : 'text-green-400 bg-green-900/20 border border-green-500/30'
                        }`}>
                        {ticket.priority === 'high' ? 'Yüksek' : ticket.priority === 'medium' ? 'Orta' : 'Düşük'}
                      </span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusClass(ticket.status)}`}>
                        {getStatusLabel(ticket.status)}
                      </span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-400 text-left">
                      {formatDate(ticket.timestamps?.createdAt || ticket.createdAt)}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <div className="flex items-center justify-start space-x-3">
                        <button
                          onClick={() => handleViewSupportTicket(ticket)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Görüntüle"
                        >
                          <FaEye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSupportTicket(ticket._id, ticket.title)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Sil"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
              >
                Önceki
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-400">
                  Sayfa <span className="font-medium text-white">{currentPage}</span> /{' '}
                  <span className="font-medium text-white">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                  >
                    Önceki
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                  >
                    Sonraki
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Support Ticket Detail Modal */}
      <SupportTicketDetail
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedTicket(null);
        }}
        supportTicket={selectedTicket}
        onUpdate={handleUpdateSupportTicket}
        onDelete={(ticketId) => {
          handleDeleteSupportTicket(ticketId);
          setShowDetailModal(false);
          setSelectedTicket(null);
        }}
        isLoading={isUpdatingTicket}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Support Ticket Sil</h3>
            <p className="text-gray-300 mb-6">
              Bu support ticket'i silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSupportTickets;
